import { withDevtools } from '@ngrx-toolkit/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import {
  ActivityInteractionTimelineEvent,
  ActivityTimeline,
  ConfigurationSetting,
  EntityEvent,
  EntryType,
  InteractionListItem,
  InteractionTimelineEvent,
  ListItem,
  RelatedEntry,
  TimelineConfiguration,
  TimelineEvent,
  User,
  ModuleType,
} from '@maximizer/core/shared/domain';
import { ShowPreviewDialogArgs } from '@maximizer/core/shared/ui/config';
import { Subject } from 'rxjs';
import { computed } from '@angular/core';
import {
  TimelineFailedAction,
  TimelineFilter,
  TimelineMainConfigurationSetting,
  TimelineGridConfigurationSetting,
  TimelineKeys,
} from './timeline.models';
import { startOfDay } from 'date-fns';
import { TimelineShowItemFor } from '../enum/timeline.enums';

export type TimelineViewState = {
  timelineEvents: TimelineEvent[];
  activities: ActivityTimeline[];
  canShowMoreUpcoming: boolean;
  currentUser: User | undefined;
  users: User[];
  interactionTypes: InteractionListItem[];
  noteAllowedTypes: ListItem<string>[] | undefined;
  selection: ActivityTimeline[] | TimelineEvent[];
  timelineConfiguration: TimelineConfiguration | null;
  searchFilter: TimelineFilter;
  mainConfigurationLoaded: boolean;
  loading: boolean;
  _loadingCount: number;
  _loadingId: number;
  showFilters: boolean;
  showAll: boolean;
  searchPhrase: string;
  hasActivityData: boolean;
  hasTimelineEventData: boolean;
  isUpcomingOverdueViewExpanded: boolean;
  entryKey: string;
  entryTimelineKeys: TimelineKeys | undefined;
  entryType: EntryType;
  moduleType: ModuleType | undefined;
  currentEntity: TimelineEvent | ActivityTimeline | undefined;
  triggerOnUpdateSingleItem: Subject<TimelineEvent | ActivityTimeline>;
  triggerOnRefresh: Subject<void>;
  triggerOnNextPage: Subject<void>;
  openRelatedEntry: Subject<RelatedEntry>;
  openEntity: Subject<EntityEvent>;
  openPreviewDialog: Subject<ShowPreviewDialogArgs>;
  editEntity: Subject<EntityEvent>;
  deleteLegacyAppointment: Subject<
    InteractionTimelineEvent | ActivityInteractionTimelineEvent
  >;
  showDeleteConfirmation: Subject<void>;
  failedAction: Subject<TimelineFailedAction>;
  loadedNextPage: Subject<void>;
  gridViewConfigurations: Partial<
    Record<string, TimelineGridConfigurationSetting>
  >;
  mainConfiguration: TimelineMainConfigurationSetting;
};

const initialShowAll = true;
const emptyGridViewConfiguration: TimelineGridConfigurationSetting = {
  columnOrder: null,
  columnSize: null,
};
const initialState: TimelineViewState = {
  timelineEvents: [],
  activities: [],
  canShowMoreUpcoming: false,
  currentUser: undefined,
  users: [],
  interactionTypes: [],
  noteAllowedTypes: [],
  selection: [],
  timelineConfiguration: null,
  searchFilter: {
    users: [],
    interactions: [],
    notes: [],
    categories: [],
    referenceDate: startOfDay(new Date()),
    showAll: initialShowAll,
    searchPhrase: '',
    showAllCategories: true,
    showItemsFor: TimelineShowItemFor.OnlyEntry,
  },
  mainConfigurationLoaded: false,
  loading: false,
  _loadingCount: 0,
  _loadingId: 0,
  hasActivityData: false,
  hasTimelineEventData: false,
  isUpcomingOverdueViewExpanded: false,
  showFilters: false,
  showAll: initialShowAll,
  searchPhrase: '',
  entryKey: '',
  entryTimelineKeys: undefined,
  entryType: 'addressBook',
  moduleType: undefined,
  currentEntity: undefined,
  triggerOnUpdateSingleItem: new Subject<TimelineEvent | ActivityTimeline>(),
  triggerOnRefresh: new Subject<void>(),
  triggerOnNextPage: new Subject<void>(),
  openRelatedEntry: new Subject<RelatedEntry>(),
  openEntity: new Subject<EntityEvent>(),
  openPreviewDialog: new Subject<ShowPreviewDialogArgs>(),
  editEntity: new Subject<EntityEvent>(),
  deleteLegacyAppointment: new Subject<
    InteractionTimelineEvent | ActivityInteractionTimelineEvent
  >(),
  showDeleteConfirmation: new Subject(),
  failedAction: new Subject<TimelineFailedAction>(),
  loadedNextPage: new Subject(),
  gridViewConfigurations: {},
  mainConfiguration: {
    main: null,
    filters: [],
  },
};

export const TimelineViewStore = signalStore(
  withDevtools('timeline-view'),
  withState(initialState),
  withComputed((state) => ({
    hasActivityData: computed(() => state.activities().length > 0),
    hasTimelineEventData: computed(() => state.timelineEvents().length > 0),
  })),
  withMethods((store) => ({
    gridViewConfigurationOf(
      moduleType: ModuleType | undefined,
    ): TimelineGridConfigurationSetting {
      return (
        store.gridViewConfigurations()[moduleType ?? ''] ??
        emptyGridViewConfiguration
      );
    },
    _patchGridViewConfiguration(
      change: Partial<TimelineGridConfigurationSetting>,
      forModuleType: ModuleType | undefined,
    ): void {
      patchState(store, (state) => {
        const moduleType = forModuleType ?? '';
        return {
          gridViewConfigurations: {
            ...state.gridViewConfigurations,
            [moduleType]: {
              ...(state.gridViewConfigurations[moduleType] ??
                emptyGridViewConfiguration),
              ...change,
            },
          },
        };
      });
    },
    beginLoading(): () => void {
      const loadingId = store._loadingId();
      let ended = false;
      patchState(store, (state) => ({
        _loadingCount: state._loadingCount + 1,
        loading: true,
      }));
      return () => {
        if (ended || loadingId !== store._loadingId()) {
          return;
        }
        ended = true;
        patchState(store, (state) => ({
          _loadingCount: state._loadingCount - 1,
          loading: state._loadingCount > 1,
        }));
      };
    },
  })),
  withMethods((store) => ({
    reset(): void {
      patchState(store, () => {
        return {
          activities: [],
          timelineEvents: [],
          canShowMoreUpcoming: false,
        };
      });
    },
    triggerRefreshEvent(force?: boolean): void {
      if (!store.loading() || force) {
        store.triggerOnRefresh().next();
      }
    },
    setActivities(activities: ActivityTimeline[]): void {
      patchState(store, () => ({
        activities,
      }));
    },
    setTimelineEvents(timelineEvents: TimelineEvent[]): void {
      patchState(store, () => ({
        timelineEvents,
      }));
    },
    setCanShowMoreUpcoming(canShowMoreUpcoming: boolean): void {
      patchState(store, () => ({
        canShowMoreUpcoming,
      }));
    },
    deleteActivities(keys: string[]): void {
      const newActivities = store
        .activities()
        .map((item) => item)
        .filter((item) => !keys.find((key) => item.key === key));
      this.setActivities(newActivities);
    },
    deleteTimelineEvents(keys: string[]): void {
      const newTimeline = store
        .timelineEvents()
        .map((item) => item)
        .filter((item) => !keys.find((key) => item.key === key));
      this.setTimelineEvents(newTimeline);
    },
    updateSelection(selection: ActivityTimeline[] | TimelineEvent[]): void {
      patchState(store, () => ({ selection }));
    },
    updateSingleItem(updatedEvent: TimelineEvent | ActivityTimeline): void {
      let result: TimelineEvent | ActivityTimeline | undefined;
      const activityEvent = store
        .activities()
        .find((activity) => activity.key === updatedEvent.key);
      result = activityEvent && Object.assign(activityEvent, updatedEvent);

      const timelineEvent = store
        .timelineEvents()
        .find((event) => event.key === updatedEvent.key);
      result =
        (timelineEvent && Object.assign(timelineEvent, updatedEvent)) ?? result;

      if (result) {
        store.triggerOnUpdateSingleItem().next(result);
      }
    },
    setCurrentUser(currentUser: User): void {
      patchState(store, () => ({
        currentUser,
      }));
    },
    setUsers(users: User[]): void {
      patchState(store, () => ({
        users,
      }));
    },
    setTimelineConfiguration(
      timelineConfiguration: TimelineConfiguration,
    ): void {
      patchState(store, () => ({
        timelineConfiguration,
      }));
    },
    setTimelineFilter(searchFilter: TimelineFilter): void {
      patchState(store, () => ({
        searchFilter,
      }));
    },
    setInteractionTypes(interactionTypes: InteractionListItem[]): void {
      patchState(store, () => ({
        interactionTypes,
      }));
    },
    setNoteAllowedTypes(
      noteAllowedTypes: ListItem<string>[] | undefined,
    ): void {
      patchState(store, () => ({
        noteAllowedTypes,
      }));
    },
    setEntryKey(entryKey: string): void {
      patchState(store, () => ({
        entryKey,
      }));
    },
    setModuleType(moduleType: ModuleType): void {
      patchState(store, () => ({
        moduleType,
      }));
    },
    setEntryTimelineKeys(entryTimelineKeys?: TimelineKeys): void {
      patchState(store, () => ({
        entryTimelineKeys,
      }));
    },
    setEntryType(entryType: EntryType): void {
      patchState(store, () => ({
        entryType,
      }));
    },
    setCurrentEntity(currentEntity: TimelineEvent | ActivityTimeline): void {
      patchState(store, () => ({
        currentEntity,
      }));
    },
    setSearchPhrase(searchPhrase: string): void {
      patchState(store, () => ({
        searchPhrase,
      }));
    },
    setShowFilters(showFilters: boolean): void {
      patchState(store, () => ({
        showFilters,
      }));
    },
    setShowAll(showAll: boolean): void {
      patchState(store, () => ({
        showAll,
        timelineEvents: [],
      }));
    },
    setMainConfigurationLoaded(mainConfigurationLoaded: boolean): void {
      patchState(store, () => ({
        mainConfigurationLoaded,
      }));
    },
    setMainConfiguration(main: ConfigurationSetting | null): void {
      patchState(store, (state) => ({
        mainConfiguration: { ...state.mainConfiguration, main },
      }));
    },
    setGridViewColumnOrder(
      columnOrder: Partial<ConfigurationSetting> | null,
      moduleType: ModuleType | undefined,
    ): void {
      store._patchGridViewConfiguration({ columnOrder }, moduleType);
    },
    setGridViewColumnSize(
      columnSize: Partial<ConfigurationSetting> | null,
      moduleType: ModuleType | undefined,
    ): void {
      store._patchGridViewConfiguration({ columnSize }, moduleType);
    },
    setGridViewUnsaved(
      unsaved: boolean,
      moduleType: ModuleType | undefined,
    ): void {
      store._patchGridViewConfiguration({ unsaved }, moduleType);
    },
    restartLoading(): () => void {
      patchState(store, (state) => ({
        _loadingId: state._loadingId + 1,
        _loadingCount: 0,
        loading: false,
      }));
      return store.beginLoading();
    },
    setIsUpcomingOverdueViewExpanded(
      isUpcomingOverdueViewExpanded: boolean,
    ): void {
      patchState(store, () => ({
        isUpcomingOverdueViewExpanded,
      }));
    },
    reinitialize(): void {
      patchState(store, (state) => ({
        ...initialState,
        gridViewConfigurations: Object.fromEntries(
          Object.entries(state.gridViewConfigurations).filter(
            ([, configuration]) => configuration?.unsaved,
          ),
        ),
        loading: state.loading,
        _loadingCount: state._loadingCount,
        _loadingId: state._loadingId,
        triggerOnUpdateSingleItem: new Subject<
          TimelineEvent | ActivityTimeline
        >(),
        triggerOnRefresh: new Subject<void>(),
        triggerOnNextPage: new Subject<void>(),
        openRelatedEntry: new Subject<RelatedEntry>(),
        openEntity: new Subject<EntityEvent>(),
        openPreviewDialog: new Subject<ShowPreviewDialogArgs>(),
        editEntity: new Subject<EntityEvent>(),
        loadedNextPage: new Subject<void>(),
        deleteLegacyAppointment: new Subject<
          InteractionTimelineEvent | ActivityInteractionTimelineEvent
        >(),
        showDeleteConfirmation: new Subject<void>(),
        failedAction: new Subject<TimelineFailedAction>(),
      }));
    },
  })),
);

export type TimelineViewStore = InstanceType<typeof TimelineViewStore>;
